/**
 * @file SDRVirtual.jsx
 * @description Página principal do sistema SDR Virtual com validação de telefone integrada
 */

// Importações principais do React e bibliotecas
import React, { useState, useEffect, useMemo } from 'react';

// Importações de componentes da biblioteca Material UI
import {
  Box,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';

// Componentes personalizados do projeto
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import PageContainer from '../components/Layout/PageContainer';
import ModoEspiao from '../components/ModoEspiao';
import WelcomeHeader from '../components/WelcomeHeader';
import Kanban from '../components/Tabs/SDR-Leads';
import SDRConfig from '../components/Tabs/SDRConfig';
import AiDirectPreVenda from './AiDirectPreVenda';
import PosVendaMensagens from '../components/Tabs/PosVendaMensagens';

// Utilitários e hooks personalizados
import { formatDate } from '../utils/date';
import useChartData from '../hooks/useChartData';
import { useFilteredLeads } from '../hooks/useFilteredLeads';
import { aggregateChartDataWithPhoneValidation } from '../hooks/aggregateChartData';
import { aggregateDiscardData } from '../hooks/aggregateDiscardData';

// Componentes das abas principais
import DashboardTab from '../components/Tabs/DashboardTab';
import LeadsTab from '../components/Tabs/LeadsTab';

// Material-UI Styling
import { ThemeProvider } from '@mui/material/styles';
import theme from '../components/theme/theme';

// Cliente HTTP para fazer requisições à API
import axios from 'axios';

// Função para decodificar o token JWT armazenado para autenticação
import { jwtDecode } from 'jwt-decode';

// Token Validation Components
import { useTokenValidation, TokenExpiredDialog } from '../utils/tokenValidation';

import { getDiasFromFiltro } from '../utils/date';
import useContratosAtivos from '../hooks/useContratosAtivos';

// Configuração dinâmica das tabs
const getTabsConfig = (contratosAtivos = []) => {
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', component: 'DashboardTab' },
    { id: 'gestao', label: 'Leads', component: 'LeadsTab' },
    { id: 'espiao', label: 'Modo Espião', component: 'ModoEspiao' },
  ];

  const temContratoAiDirect = contratosAtivos.some(
    contrato => Number(contrato?.idProduto ?? contrato?.id_produto) === 8
  );

  if (temContratoAiDirect) {
    tabs.push({ id: 'aidirect', label: 'aiDirect', component: 'AiDirect' });
  }

  const temContratoContextoDinamico = contratosAtivos.some(
    contrato => contrato.idProduto === 2 && contrato.contextoDinamico === "1"
  );

  if (temContratoContextoDinamico) {
    tabs.push({ id: 'config', label: 'Configuração', component: 'SDRConfig' });
  }

  if (temContratoContextoDinamico) {
    tabs.push({ id: 'whatsapp', label: 'Whatsapp', component: 'WhatsappTemplates' });
  }

  return tabs;
};

const SITUACOES_BASE = [
  'Em Atendimento',
  'Qualificado',
  'Descartado',
  'Não é WhatsApp',
  'Retirado da etapa IA',
  'Outros Sistemas',
  'Novo Lead',
  'Cadência'
];

const LEADS_VIEW_MODE_STORAGE_KEY = 'sdr_leads_view_mode';

/**
 * Componente principal da página SDR Virtual com validação de telefone
 */
const SDRVirtual = () => {
  // ========== VALIDAÇÃO DE TOKEN ==========
  const { showExpiredDialog, countdown, redirectToHome } = useTokenValidation();

  // ========== ESTADOS ==========

  // Estados para controlar navegação e filtros
  const [activeTabId, setActiveTabId] = useState('dashboard');
  const [dateFilter, setDateFilter] = useState('Mês atual');
  const [situacaoFilter, setSituacaoFilter] = useState('Todos');
  const [idLeadFilter, setIdLeadFilter] = useState('');
  const [nomeFilter, setNomeFilter] = useState('');
  const [whatsAppIdFilter, setWhatsAppIdFilter] = useState('');
  const [descarteFilter, setDescarteFilter] = useState(''); // Estado para filtro de descarte
  const [empreendimentoFilter, setEmpreendimentoFilter] = useState('');
  const [isTabLoading, setIsTabLoading] = useState(false);

  // Novo estado para controlar a validação de telefone
  const [phoneValidationEnabled, setPhoneValidationEnabled] = useState(true);

  // Estados para a aba de Gestão do SDR
  const [leadsGestao, setLeadsGestao] = useState([])
  const [idCRMFromParams, setIdCRMFromParams] = useState(null);
  // Novo estado para controlar o loading específico da aba de Gestão
  const [, setIsGestaoLoading] = useState(false);
  const [isGestaoTableLoading, setIsGestaoTableLoading] = useState(false);

  // Estados para armazenar dados de contatos e controle do Modo Espião
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);

  // Estados para os filtros de data Dashboard
  const [startDate, setStartDate] = useState(() => {
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]; // 'YYYY-MM-DD'
  });

  const [endDate, setEndDate] = useState('');

  const [forceTokenExpired, setForceTokenExpired] = useState(false);

  const [tempDateFilter, setTempDateFilter] = useState('Mês atual');
  const [tempSituacaoFilter, setTempSituacaoFilter] = useState('Todos');
  const [tempIdLeadFilter, setTempIdLeadFilter] = useState('');
  const [tempNomeFilter, setTempNomeFilter] = useState('');
  const [tempWhatsAppIdFilter, setTempWhatsAppIdFilter] = useState('');
  const [tempDescarteFilter, setTempDescarteFilter] = useState('');
  const [tempEmpreendimentoFilter, setTempEmpreendimentoFilter] = useState('');
  const [tempStartDate, setTempStartDate] = useState(() => {
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  });
  const [tempEndDate, setTempEndDate] = useState('');

  const [tempKanbanDateFilter, setTempKanbanDateFilter] = useState('Mês atual');
  const [tempKanbanStartDate, setTempKanbanStartDate] = useState('');
  const [tempKanbanEndDate, setTempKanbanEndDate] = useState('');

  const [alterarDatasEspecificas, setAlterarDatasEspecificas] = useState(false)

  // Configuração dinâmica das tabs baseada nos contratos ativos do cliente
  const { contratosAtivos } = useContratosAtivos();

  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') {
      return 'kanban';
    }
    const storedView = window.localStorage.getItem(LEADS_VIEW_MODE_STORAGE_KEY);
    return storedView === 'table' || storedView === 'kanban' ? storedView : 'kanban';
  })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(LEADS_VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // Configuração dinâmica das tabs baseada na role do usuário E contratos
  const tabsConfig = useMemo(() => getTabsConfig(contratosAtivos), [contratosAtivos]);
  const currentTabIndex = tabsConfig.findIndex(tab => tab.id === activeTabId);

  // URL base da API
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // ========== HOOKS E DADOS DERIVADOS ==========

  /**
   * Hook customizado que busca os dados de gráficos e leads da API
   */
  const { chartData, loadingChart } = useChartData(
    activeTabId,  // ← Novo - usando o ID da tab ativa
    dateFilter,
    baseURL,
    startDate,
    endDate,
    setForceTokenExpired,
    alterarDatasEspecificas,
    setAlterarDatasEspecificas
  );

  /**
   * Effect para carregar dados do Agente de Conteúdo quando a aba de Gestão estiver ativa
   * ATUALIZADO: Com loading específico para dados da Gestão
   */

  useEffect(() => {
    if (activeTabId === 'gestao') {

      const fetchContentAgentData = async () => {
        try {
          setIsGestaoLoading(true);
          setIsGestaoTableLoading(true); // Adicionar esta linha

          const token = localStorage.getItem('jwtToken');
          if (!token) {
            console.error("Token não encontrado no localStorage.");
            return;
          }

          const decodedToken = jwtDecode(token);
          const idClienteAipim = decodedToken.id_cliente;
          const dias = dateFilter === 'Anual' ? null : getDiasFromFiltro(dateFilter);

          const params = {
            idCliente: idClienteAipim,
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
            dias
          };


          const response = await axios.get(`${baseURL}/produto/sdr/leads-cliente`, {
            params,
            headers: { Authorization: `Bearer ${token}` }
          });

          const sortedData = response.data.sort((a, b) => {
            return new Date(b.dataHoraCadastroAtualizado) - new Date(a.dataHoraCadastroAtualizado);
          });

          setLeadsGestao(sortedData);
        } catch (error) {
          console.error("Erro ao buscar dados do agente de conteúdo:", error);
          if (error.response?.status === 401 ||
            error.response?.data?.message?.toLowerCase().includes("token")) {
            setForceTokenExpired(true);
          }
        } finally {
          setIsGestaoLoading(false);
          // Adicionar um pequeno delay para dar tempo da tabela processar os dados
          setTimeout(() => {
            setIsGestaoTableLoading(false);
          }, 300);
        }
      };

      fetchContentAgentData();
    }
  }, [activeTabId, baseURL, startDate, endDate, dateFilter]);

  /**
   * Hook customizado que filtra os leads com validação de telefone
   */
  const filteredLeads = useFilteredLeads(leadsGestao, {
    situacaoFilter,
    idLeadFilter,
    nomeFilter,
    whatsAppIdFilter,
    descarteFilter,
    empreendimentoFilter,  // ADICIONAR ESTA LINHA
    phoneValidationEnabled,
    startDate,
    endDate
  });

  useEffect(() => {
    if (activeTabId !== 'gestao') {
      return;
    }

    if (filteredLeads.length > 0 || !baseURL || idCRMFromParams !== null) {
      return;
    }

    const fetchIdCrm = async () => {
      try {
        const token = localStorage.getItem('jwtToken');
        if (!token) {
          console.error("Token não encontrado no localStorage.");
          return;
        }

        const decodedToken = jwtDecode(token);
        const idClienteAipim = decodedToken.id_cliente;

        const response = await axios.get(`${baseURL}/produto/sdr/parametros`, {
          params: { idClienteAipim },
          headers: { Authorization: `Bearer ${token}` }
        });

        const idCrm = response?.data?.idCrm ?? response?.data?.sdrConfig?.idCrm;
        if (idCrm !== undefined && idCrm !== null) {
          setIdCRMFromParams(idCrm);
        }
      } catch (error) {
        console.error("Erro ao buscar parâmetros do SDR:", error);
        if (
          error.response?.status === 401 ||
          error.response?.data?.message?.toLowerCase().includes("token")
        ) {
          setForceTokenExpired(true);
        }
      }
    };

    fetchIdCrm();
  }, [activeTabId, baseURL, filteredLeads.length, idCRMFromParams, setForceTokenExpired]);

  /**
   * Memoiza os dados agregados para o gráfico com validação de telefone
   */
  const aggregatedChartData = useMemo(() => {
    return aggregateChartDataWithPhoneValidation(chartData);
  }, [chartData]);

  const totalLeads = aggregatedChartData.data[aggregatedChartData.labels.indexOf("Total")] || 0;
  const validos = aggregatedChartData.data[aggregatedChartData.labels.indexOf("Leads Válidos")] || 0;
  const qualificados = aggregatedChartData.data[aggregatedChartData.labels.indexOf("Qualificado")] || 0;

  /**
   * Calcula a taxa de conversão como porcentagem
   */
  const conversionRate = validos > 0
    ? (qualificados / validos) * 100
    : 0;

  /**
   * Efeito que busca a lista de contatos da API quando o usuário
   * navega para a aba Modo Espião
   */
  useEffect(() => {
    if (activeTabId === 'espiao') {
      const fetchContacts = async () => {
        try {
          const token = localStorage.getItem('jwtToken');

          if (token) {
            const decodedToken = jwtDecode(token);
            const idClienteAipim = decodedToken.id_cliente;

            const response = await axios.get(`${baseURL}/produto/sdr/lista-contatos`, {
              params: { idClienteAipim },
              headers: { Authorization: `Bearer ${token}` }
            });
            setContacts(response.data);
          } else {
            console.error("Token não encontrado no localStorage.");
          }
        } catch (error) {
          console.error("Erro ao buscar contatos.");

          if (
            error.response?.status === 401 ||
            error.response?.data?.message?.toLowerCase().includes("token") // ou "expired"
          ) {
            setForceTokenExpired(true); // Força abrir o diálogo
          }
        }
      };

      fetchContacts();
    }
  }, [activeTabId, baseURL]);



  // ========== MANIPULADORES DE EVENTOS ==========

  /**
   * Manipulador para troca de abas
   * ATUALIZADO: Loading condicional baseado na aba
   */
  const handleTabChange = (event, newIndex) => {
    setIsTabLoading(true);

    const selectedTab = tabsConfig[newIndex];
    if (selectedTab) {
      setActiveTabId(selectedTab.id);
    }

    setTimeout(() => {
      setIsTabLoading(false);
    }, 500);
  };

  /**
   * Função para resetar todos os filtros
   */
  const handleClearFilters = () => {
    // Limpar filtros temporários
    setTempDateFilter('Mês atual');
    setTempStartDate('');
    setTempEndDate('');
    setTempSituacaoFilter('Todos');
    setTempIdLeadFilter('');
    setTempNomeFilter('');
    setTempWhatsAppIdFilter('');
    setTempDescarteFilter('');
    setTempEmpreendimentoFilter('');

    // Limpar filtros reais
    setDateFilter('Mês atual');
    setStartDate('');
    setEndDate('');
    setSituacaoFilter('Todos');
    setIdLeadFilter('');
    setNomeFilter('');
    setWhatsAppIdFilter('');
    setDescarteFilter('');
    setEmpreendimentoFilter('');
    setPhoneValidationEnabled(true);
  };

  // Adicionar após handleClearFilters:
  const handleApplyFilters = () => {
    setDateFilter(tempDateFilter);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setSituacaoFilter(tempSituacaoFilter);
    setIdLeadFilter(tempIdLeadFilter);
    setNomeFilter(tempNomeFilter);
    setWhatsAppIdFilter(tempWhatsAppIdFilter);
    setDescarteFilter(tempDescarteFilter);
    setEmpreendimentoFilter(tempEmpreendimentoFilter);
    setAlterarDatasEspecificas(true)
  };

  const handleYearFilterChange = (year) => {
    if (!year) return;

    const yearString = String(year);
    const dataInicio = `${yearString}-01-01`;
    const dataFim = `${yearString}-12-31`;

    // Atualiza filtros temporários (para UI)
    setTempDateFilter('Anual');
    setTempStartDate(dataInicio);
    setTempEndDate(dataFim);

    // Atualiza filtros reais (que vão para o backend)
    setDateFilter('Anual');
    setStartDate(dataInicio);
    setEndDate(dataFim);

    // Garante que o Dashboard possa refazer a consulta
    setAlterarDatasEspecificas(true);
  };


  const handleApplyKanbanFilters = () => {
    setDateFilter(tempKanbanDateFilter);
    setStartDate(tempKanbanStartDate);
    setEndDate(tempKanbanEndDate);
    setAlterarDatasEspecificas(true);
  };

  /**
   * Manipulador para clique no WhatsAppId
   */
  const handleWhatsAppIdClick = (lead) => {
    setSelectedContact(lead);
    setActiveTabId('espiao'); // Trocar setTabIndex(3) por isso
  };

  /**
   * Memoiza os dados agregados de motivos de descarte para gráfico
   * Evita recomputação desnecessária quando outras partes do estado mudam
   */
  const aggregatedDiscardData = useMemo(() => {
    return aggregateDiscardData(chartData);
  }, [chartData]);

  /**
 * Calcula motivos de descarte únicos dos leads filtrados por período
 * MODIFICADO: Usar filtros temporários para calcular motivos disponíveis
 */
  const motivosDescarteDisponiveis = useMemo(() => {
    if (!leadsGestao || leadsGestao.length === 0) return [];

    // Usar as datas temporárias para calcular os motivos disponíveis
    const startDateToUse = tempDateFilter === 'Datas Específicas' ? tempStartDate : startDate;
    const endDateToUse = tempDateFilter === 'Datas Específicas' ? tempEndDate : endDate;

    // Filtrar leads por período primeiro
    const leadsNoPeriodo = leadsGestao.filter(lead => {
      if (!startDateToUse && !endDateToUse) return true;

      const leadDate = new Date(lead.dataHoraCadastroAtualizado).toISOString().split('T')[0];

      if (startDateToUse && endDateToUse) {
        return leadDate >= startDateToUse && leadDate <= endDateToUse;
      } else if (startDateToUse) {
        return leadDate >= startDateToUse;
      } else if (endDateToUse) {
        return leadDate <= endDateToUse;
      }

      return true;
    });

    // Extrair motivos únicos dos leads descartados no período
    const motivos = leadsNoPeriodo
      .filter(lead => lead.situacaoInterna === 'Descartado' && lead.motivoDescarte)
      .map(lead => lead.motivoDescarte.trim())
      .filter((motivo, index, array) => motivo && array.indexOf(motivo) === index)
      .sort();

    motivos.push("Sem Motivo");

    return motivos;
  }, [leadsGestao, tempStartDate, tempEndDate, tempDateFilter, startDate, endDate]); // Adicionar dependências dos filtros temporários

  /**
 * Calcula empreendimentos únicos dos leads filtrados por período
 */
  const empreendimentosDisponiveis = useMemo(() => {
    if (!leadsGestao || leadsGestao.length === 0) return [];

    const startDateToUse = tempDateFilter === 'Datas Específicas' ? tempStartDate : startDate;
    const endDateToUse = tempDateFilter === 'Datas Específicas' ? tempEndDate : endDate;

    // Filtrar leads por período primeiro
    const leadsNoPeriodo = leadsGestao.filter(lead => {
      if (!startDateToUse && !endDateToUse) return true;

      const leadDate = new Date(lead.dataHoraCadastroAtualizado).toISOString().split('T')[0];

      if (startDateToUse && endDateToUse) {
        return leadDate >= startDateToUse && leadDate <= endDateToUse;
      } else if (startDateToUse) {
        return leadDate >= startDateToUse;
      } else if (endDateToUse) {
        return leadDate <= endDateToUse;
      }

      return true;
    });

    // Extrair empreendimentos únicos
    const empreendimentos = leadsNoPeriodo
      .filter(lead => lead.nomeEmpreendimento)
      .map(lead => lead.nomeEmpreendimento.trim())
      .filter((nome, index, array) => nome && array.indexOf(nome) === index)
      .sort();

    return empreendimentos;
  }, [leadsGestao, tempStartDate, tempEndDate, tempDateFilter, startDate, endDate]);

  const situacoesDisponiveis = useMemo(() => {
    const set = new Set(SITUACOES_BASE);

    if (leadsGestao && leadsGestao.length > 0) {
      leadsGestao.forEach((lead) => {
        if (lead.situacaoInterna) {
          set.add(lead.situacaoInterna.trim());
        }
      });
    }

    return Array.from(set).sort();
  }, [leadsGestao]);


  /**
   * Função para determinar se deve mostrar loading
   * NOVA: Lógica condicional para diferentes tipos de loading
   */
  const shouldShowLoading = () => {
    // Para outras abas, usa o loading genérico de troca de abas
    return isTabLoading;
  };

  useEffect(() => {
    const applyFilters = () => {
      setDateFilter(tempDateFilter);
      setStartDate(tempStartDate);
      setEndDate(tempEndDate);
      setSituacaoFilter(tempSituacaoFilter);
      setIdLeadFilter(tempIdLeadFilter);
      setNomeFilter(tempNomeFilter);
      setWhatsAppIdFilter(tempWhatsAppIdFilter);
      setDescarteFilter(tempDescarteFilter);
      setEmpreendimentoFilter(tempEmpreendimentoFilter);  // ADICIONAR
      setAlterarDatasEspecificas(true);
    };

    if (
      activeTabId === 'dashboard' &&
      tempDateFilter !== dateFilter &&
      tempDateFilter !== 'Datas Específicas' &&
      tempDateFilter !== 'Anual'              // NOVA condição
    ) {
      applyFilters();
    }

  }, [tempDateFilter, dateFilter, tempStartDate, tempEndDate, tempSituacaoFilter, tempIdLeadFilter, tempNomeFilter, tempWhatsAppIdFilter, tempDescarteFilter, tempEmpreendimentoFilter, activeTabId]);  // ADICIONAR tempEmpreendimentoFilter

  // useEffect para aplicar filtros automaticamente na aba de Leads quando o período muda
  useEffect(() => {
    const applyLeadsFilters = () => {
      setDateFilter(tempDateFilter);
      setStartDate(tempStartDate);
      setEndDate(tempEndDate);
      setSituacaoFilter(tempSituacaoFilter);
      setIdLeadFilter(tempIdLeadFilter);
      setNomeFilter(tempNomeFilter);
      setWhatsAppIdFilter(tempWhatsAppIdFilter);
      setDescarteFilter(tempDescarteFilter);
      setEmpreendimentoFilter(tempEmpreendimentoFilter);
    };

    // Aplicar automaticamente quando:
    // 1. Está na aba de Leads (gestao)
    // 2. O filtro de período mudou
    // 3. Não é "Datas Específicas" (que requer botão "Aplicar Filtros")
    // 4. Para "Anual", só aplicar se as datas estiverem definidas (ano selecionado)
    if (
      activeTabId === 'gestao' &&
      tempDateFilter !== dateFilter &&
      tempDateFilter !== 'Datas Específicas'
    ) {
      // Para "Anual", só aplicar se as datas estiverem definidas
      if (tempDateFilter === 'Anual') {
        if (tempStartDate && tempEndDate) {
          applyLeadsFilters();
        }
      } else {
        // Para outros filtros, aplicar automaticamente
        applyLeadsFilters();
      }
    }
  }, [tempDateFilter, dateFilter, tempStartDate, tempEndDate, tempSituacaoFilter, tempIdLeadFilter, tempNomeFilter, tempWhatsAppIdFilter, tempDescarteFilter, tempEmpreendimentoFilter, activeTabId]);

  useEffect(() => {
    if (tempDateFilter !== 'Datas Específicas') {
      setTempStartDate('');
      setTempEndDate('');
    }
  }, [tempDateFilter]);

  useEffect(() => {
    const applyKanbanFilters = () => {
      setDateFilter(tempKanbanDateFilter);
      setStartDate(tempKanbanStartDate);
      setEndDate(tempKanbanEndDate);
      setAlterarDatasEspecificas(true);
    };

    // Aplicar automaticamente quando mudar o filtro de período (exceto "Datas Específicas")
    if (activeTabId === 'kanban' && tempKanbanDateFilter !== dateFilter && tempKanbanDateFilter !== 'Datas Específicas') {
      applyKanbanFilters();
    }
  }, [tempKanbanDateFilter, dateFilter, tempKanbanStartDate, tempKanbanEndDate, activeTabId]);

  // ADICIONAR ESTE useEffect:
  useEffect(() => {
    if (activeTabId === 'kanban' && tempKanbanDateFilter !== 'Datas Específicas') {
      setTempKanbanStartDate('');
      setTempKanbanEndDate('');
    }
  }, [tempKanbanDateFilter, activeTabId]);

  //console.log(JSON.parse(localStorage.getItem('contratosAtivos') || '[]'))

  // ========== RENDERIZAÇÃO ==========
  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: '100vh',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'background.default'
        }}
      >
        <Sidebar
          sx={{
            width: { xs: '100%', md: 280 },
            flexShrink: 0,
            order: { xs: 2, md: 1 },
          }}
        />

        <Box
          sx={{
            flexGrow: 1,
            order: { xs: 1, md: 2 },
            width: '100%',
          }}
        >
          <Header
            title={'Pr\u00e9 Venda'}
            sx={{
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 1.5, md: 2 },
            }}
          />

          <PageContainer
            sx={{
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 1.25, md: 1.5 },
              mb: { xs: 2, md: 0 },
            }}
          >
            <WelcomeHeader
              title={'Pr\u00e9 Venda'}
              subtitle=''
            />

            {/* Abas de navegacao */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: { xs: 2, md: 3 },
                px: { xs: 1, md: 1.2 },
                mb: { xs: 2, md: 2.4 },
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Tabs
                value={currentTabIndex}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                aria-label="Abas de navegacao"
                variant="scrollable"
                sx={{
                  minHeight: 48,
                  '& .MuiTab-root': {
                    minHeight: 48,
                    textTransform: 'none',
                    fontWeight: 700,
                  },
                }}
              >
                {tabsConfig.map((tab) => (
                  <Tab
                    key={tab.id}
                    label={tab.label}
                  />
                ))}
              </Tabs>
            </Box>

            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: { xs: 2, md: 3 },
                p: { xs: 2, sm: 3, md: 3 },
                pb: { xs: 6, sm: 3, md: 3 },
                boxShadow: {
                  xs: '0 2px 10px rgba(0,0,0,0.08)',
                  md: '0 4px 20px rgba(0,0,0,0.08)'
                },
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >

              {/* Loading condicional baseado na aba atual */}
              {shouldShowLoading() ? (
                <Box sx={{ mt: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <CircularProgress size={40} thickness={4} color="primary" />
                  <Box sx={{ mt: 2, fontSize: '1rem', color: 'text.secondary' }}>
                    {'Carregando...'}
                  </Box>
                </Box>
              ) : (
                <>
                  {activeTabId === 'dashboard' && (
                    <DashboardTab
                      dateFilter={dateFilter}
                      setDateFilter={setDateFilter}
                      tempDateFilter={tempDateFilter}           // Nova prop
                      setTempDateFilter={setTempDateFilter}     // Nova prop
                      tempStartDate={tempStartDate}             // Nova prop
                      setTempStartDate={setTempStartDate}       // Nova prop
                      tempEndDate={tempEndDate}                 // Nova prop
                      setTempEndDate={setTempEndDate}           // Nova prop
                      handleApplyFilters={handleApplyFilters}   // Nova prop
                      loadingChart={loadingChart}
                      aggregatedChartData={aggregatedChartData}
                      totalLeads={totalLeads}
                      conversionRate={conversionRate}
                      aggregatedDiscardData={aggregatedDiscardData}
                      startDate={startDate}
                      setStartDate={setStartDate}
                      endDate={endDate}
                      setEndDate={setEndDate}
                      onYearChange={handleYearFilterChange}
                    />
                  )}

                  {activeTabId === 'gestao' && (
                    <LeadsTab
                      allLeads={leadsGestao}
                      filteredLeads={filteredLeads}
                      isInitialLoading={isGestaoTableLoading}
                      // Props dos filtros reais (para filtragem)
                      idLeadFilter={idLeadFilter}
                      nomeFilter={nomeFilter}
                      whatsAppIdFilter={whatsAppIdFilter}
                      situacaoFilter={situacaoFilter}
                      descarteFilter={descarteFilter}
                      motivosDescarte={motivosDescarteDisponiveis}
                      startDate={startDate}
                      endDate={endDate}
                      dateFilter={dateFilter}
                      // Props dos filtros temporários (para exibição)
                      tempIdLeadFilter={tempIdLeadFilter}
                      tempNomeFilter={tempNomeFilter}
                      tempWhatsAppIdFilter={tempWhatsAppIdFilter}
                      tempSituacaoFilter={tempSituacaoFilter}
                      tempDescarteFilter={tempDescarteFilter}
                      tempEmpreendimentoFilter={tempEmpreendimentoFilter}
                      setTempEmpreendimentoFilter={setTempEmpreendimentoFilter}
                      empreendimentoFilter={empreendimentoFilter}
                      empreendimentosDisponiveis={empreendimentosDisponiveis}
                      tempStartDate={tempStartDate}
                      tempEndDate={tempEndDate}
                      tempDateFilter={tempDateFilter}
                      setTempIdLeadFilter={setTempIdLeadFilter}
                      setTempNomeFilter={setTempNomeFilter}
                      setTempWhatsAppIdFilter={setTempWhatsAppIdFilter}
                      setTempSituacaoFilter={setTempSituacaoFilter}
                      setTempDescarteFilter={setTempDescarteFilter}
                      setTempStartDate={setTempStartDate}
                      setTempEndDate={setTempEndDate}
                      setTempDateFilter={setTempDateFilter}
                      handleApplyFilters={handleApplyFilters}
                      handleClearFilters={handleClearFilters}
                      setDescarteFilter={setTempDescarteFilter}
                      handleWhatsAppIdClick={handleWhatsAppIdClick}
                      formatDate={formatDate}
                      phoneValidationEnabled={phoneValidationEnabled}
                      setPhoneValidationEnabled={setPhoneValidationEnabled}
                      setForceTokenExpired={setForceTokenExpired}
                      setStartDate={setStartDate}
                      setEndDate={setEndDate}
                      viewMode={viewMode}
                      setViewMode={setViewMode}
                      onYearChange={handleYearFilterChange}
                      situacoesDisponiveis={situacoesDisponiveis}
                      idCRM={idCRMFromParams}
                    />
                  )}


                  {activeTabId === 'kanban' && (
                    <Kanban
                      dateFilter={dateFilter}
                      setDateFilter={setDateFilter}
                      tempDateFilter={tempKanbanDateFilter}
                      setTempDateFilter={setTempKanbanDateFilter}
                      tempStartDate={tempKanbanStartDate}
                      setTempStartDate={setTempKanbanStartDate}
                      tempEndDate={tempKanbanEndDate}
                      setTempEndDate={setTempKanbanEndDate}
                      handleApplyFilters={handleApplyKanbanFilters}
                      startDate={startDate}
                      setStartDate={setStartDate}
                      endDate={endDate}
                      setEndDate={setEndDate}
                      setForceTokenExpired={setForceTokenExpired}
                      handleWhatsAppIdClick={handleWhatsAppIdClick}
                    />
                  )}

                  {activeTabId === 'config' && (
                    <SDRConfig produto='sdr' />
                  )}

                  {activeTabId === 'whatsapp' && (
                    <PosVendaMensagens templatesEndpoint="/produto/sdr/agente/whatsapp/templates" />
                  )}

                  {activeTabId === 'espiao' && (
                    <Box sx={{ mt: { xs: 2, md: 3 } }}>
                      <ModoEspiao
                        contacts={contacts}
                        selectedContact={selectedContact}
                        onSelectContact={setSelectedContact}
                        currentTab="agente-pre-venda"
                        setForceTokenExpired={setForceTokenExpired}
                      />
                    </Box>
                  )}

                  {activeTabId === 'aidirect' && (
                    <Box sx={{ mt: { xs: 2, md: 3 } }}>
                      <AiDirectPreVenda setForceTokenExpired={setForceTokenExpired} />
                    </Box>
                  )}
                </>
              )}

            </Box>
          </PageContainer>
        </Box>
      </Box>

      {/* ========== DIALOG DE TOKEN EXPIRADO - GLOBAL ========== */}
      {/* 
        POSICIONADO AQUI para aparecer independente da aba ativa
        Este dialog ficará sempre no nível mais alto do componente
      */}
      <TokenExpiredDialog
        open={showExpiredDialog || forceTokenExpired}
        countdown={countdown}
        onRedirect={redirectToHome}
      />
    </ThemeProvider>
  );
};

export default SDRVirtual;
